import { observable, makeObservable, action } from "mobx";
import { inject, observer } from "mobx-react";
import { Component } from "react";
import { FormattedMessage, type IntlShape, injectIntl } from "react-intl";
import { compose } from "nvent-web/utils/compose";

import PageTitle from "nvent-web/App/components/PageTitle";
import ProjectForm from "nvent-web/components/ProjectForm";
import Api from "nvent-web/services/Api";
import { NotificationsStore } from "nvent-web/stores/Notifications";
import { ProjectsStore } from "nvent-web/stores/Projects";
import { type NewProjectFormValues } from "nvent-web/types/NewProjectFormValues";
import {
  clearAwaitingProject,
  getAwaitingProject,
} from "nvent-web/utils/awaitingProject";

import style from "./ContinueCreatingProject.module.scss";
import { withRouter, type WithRouterProps } from "~/nvent-web/utils/withRouter";

const emptyValues: NewProjectFormValues = {
  project: {
    name: "",
    addressAttributes: {
      streetAddress: "",
      postalCode: "",
      locality: "",
    },
    clientName: "",
    clientCompany: "",
    clientEmail: "",
    clientPhoneNumber: "",
  },
};

interface ContinueCreatingProjectInnerProps extends WithRouterProps {
  intl: IntlShape;
  projects: ProjectsStore;
  api: Api;
  notifications: NotificationsStore;
}

class ContinueCreatingProjectInner extends Component<ContinueCreatingProjectInnerProps> {
  private initialValues?: NewProjectFormValues;

  private isSubmitting = false;

  constructor(props: ContinueCreatingProjectInnerProps) {
    super(props);

    makeObservable<
      ContinueCreatingProjectInner,
      "initialValues" | "isSubmitting"
    >(this, {
      initialValues: observable,
      isSubmitting: observable,
      setInitialValues: action.bound,
      setIsSubmitting: action.bound,
    });
  }

  setIsSubmitting(value: typeof this.isSubmitting) {
    this.isSubmitting = value;
  }

  componentDidMount() {
    const awaitingProject = getAwaitingProject();

    if (awaitingProject) {
      this.setInitialValues({
        project: {
          ...emptyValues.project,
          name: awaitingProject.roomDetails.projectName || "",
        },
      });
    }
  }

  render() {
    if (!this.initialValues) {
      return null;
    }

    return (
      <div className={style.container}>
        <PageTitle>
          <FormattedMessage id="projects.continue.title" />
        </PageTitle>
        <ProjectForm
          handleSubmit={this.handleSubmit}
          initialValues={this.initialValues}
          onCancel={this.handleCancel}
          isSubmitting={this.isSubmitting}
        />
      </div>
    );
  }

  setInitialValues(value: typeof this.initialValues) {
    this.initialValues = value;
  }
  private handleSubmit = async (values: NewProjectFormValues) => {
    this.setIsSubmitting(true);
    const { api, projects } = this.props;

    try {
      const awaitingProject = getAwaitingProject();

      if (awaitingProject) {
        const { id: projectId } = await api.projects
          .create(values)
          .then(({ data }) => data);
        const { id: roomId } = await api.rooms
          .create({
            data: {
              name: this.props.intl.formatMessage({ id: "room.defaultName" }),
              ...awaitingProject.roomDetails,
            },
            projectId,
          })
          .then(({ data }) => data);

        await api.products.createSkus(
          projectId,
          roomId,
          awaitingProject.productSkus
        );

        clearAwaitingProject();
        projects.loadProjects();
        this.props.navigate(`/projects/${projectId}/rooms/${roomId}`);
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_error) {
      this.props.notifications.createError(
        <FormattedMessage id="error.somethingWentWrong" />
      );
    } finally {
      this.setIsSubmitting(false);
    }
  };

  private handleCancel = () => {
    clearAwaitingProject();
    this.props.navigate(`/projects/open`);
  };
}

export const ContinueCreatingProject = compose(
  inject("projects", "notifications", "api"),
  injectIntl,
  withRouter,
  observer
)(ContinueCreatingProjectInner);
